import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Navbar } from '../components/Navbar';
import { EditChoiceModal } from '../components/EditChoiceModal';
import { SendEmailModal } from '../components/SendEmailModal';
import {
  ClinicalVisit,
  FullPatient,
  getClinicalVisitsByPatient,
  getPatient,
} from '../services/api';
import { useWorkOrderPDF } from '../hooks/useWorkOrderPDF';

interface LocationState {
  patientName?: string;
}

const formatDate = (value?: string | null, showToday: boolean = true) => {
  if (!value) return '';
  const date = new Date(value);
  const today = new Date();
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  if (isToday && showToday) return 'Hoy';

  return date.toLocaleDateString('es-GT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '';
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2,
  }).format(value);
};

const getStatusLabel = (status: 'draft' | 'sale_pending' | 'payment_pending' | 'completed') => {
  if (status === 'sale_pending') return 'Venta Pendiente';
  if (status === 'payment_pending') return 'Pago Pendiente';
  if (status === 'completed') return 'Completado';
  if (status === 'draft') return 'Borrador';
  return status;
};

const getBadgeStyles = (status: 'draft' | 'sale_pending' | 'payment_pending' | 'completed') => {
  if (status === 'sale_pending') {
    return {
      container: 'bg-[#FFC107]/20 border-[#FFC107]',
      text: 'text-[#FF8F00]',
      icon: '⚠️',
    };
  }

  if (status === 'payment_pending') {
    return {
      container: 'bg-orange-100 border-orange-300',
      text: 'text-orange-700',
      icon: '💰',
    };
  }

  if (status === 'completed') {
    return {
      container: 'bg-green-100 border-green-200',
      text: 'text-green-700',
      icon: '✔️',
    };
  }

  if (status === 'draft') {
    return {
      container: 'bg-slate-100 border-slate-200',
      text: 'text-slate-700',
      icon: '✏️',
    };
  }

  return {
    container: 'bg-slate-100 border-slate-200',
    text: 'text-slate-700',
    icon: '•',
  };
};

const historyTitleForVisit = (visit: ClinicalVisit) => {
  if (visit.diagnosis_snippet) return visit.diagnosis_snippet;
  if (visit.status === 'sale_pending') return 'Examen Completo';
  if (visit.status === 'draft') return 'Borrador';
  return 'Visita Clínica';
};

export const PatientHistory = () => {
  const { patient_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { patientName: patientNameFromState } = (location.state as LocationState) || {};
  const { generatePDF, isGenerating } = useWorkOrderPDF();

  const [patientData, setPatientData] = useState<FullPatient | null>(null);
  const [patientName, setPatientName] = useState<string>(patientNameFromState || 'Paciente');
  const [visits, setVisits] = useState<ClinicalVisit[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState<boolean>(false);
  const [selectedEmailVisitId, setSelectedEmailVisitId] = useState<number | null>(null);

  const sortedVisits = useMemo(
    () => [...visits].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bDate - aDate;
    }),
    [visits]
  );

  useEffect(() => {
    const loadHistory = async () => {
      if (!patient_id) return;
      setLoading(true);
      setError(null);

      try {
        // Always fetch patient data for email prefill and other details
        const patient = await getPatient(String(patient_id));
        setPatientData(patient);
        if (!patientNameFromState) {
          setPatientName(patient.full_name);
        }

        const visits = await getClinicalVisitsByPatient(patient_id);
        setVisits(visits);
      } catch (err) {
        console.error('Error loading patient history:', err);
        setError(err instanceof Error ? err.message : 'No se pudo cargar el historial');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [patient_id, patientNameFromState]);

  const activeVisit = sortedVisits.find((visit) => visit.status === 'sale_pending');
  const pendingPaymentVisits = sortedVisits.filter((visit) => visit.status === 'payment_pending');
  const pastVisits = sortedVisits.filter((visit) => visit.id !== activeVisit?.id && visit.status !== 'payment_pending');

  const handleBack = () => {
    navigate(-1);
  };

  const handleNewExam = () => {
    navigate(`/clinical-chart/${patient_id}/new`);
  };

  const handleViewVisit = (visitId: number) => {
    navigate(`/clinical-chart/${patient_id}/${visitId}`);
  };

  const handleEditVisit = (visitId: number) => {
    setSelectedVisitId(visitId);
    setIsEditModalOpen(true);
  };

  const handleSelectClinicalChart = () => {
    if (selectedVisitId) {
      navigate(`/clinical-chart/${patient_id}/${selectedVisitId}`, { state: { editMode: true } });
    }
    setIsEditModalOpen(false);
  };

  const handleSelectSales = () => {
    if (selectedVisitId) {
      navigate(`/commercial-handoff/${selectedVisitId}`);
    }
    setIsEditModalOpen(false);
  };

  const handlePrintWorkOrder = async (visitId: number) => {
    try {
      await generatePDF(visitId);
    } catch (err) {
      console.error('Error generating PDF:', err);
    }
  };

  const handleSendEmail = (visitId: number) => {
    setSelectedEmailVisitId(visitId);
    setIsSendEmailModalOpen(true);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#f6f7f8]">
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <Navbar />
      </div>

      <main className="flex flex-1 justify-center px-4 pb-16 sm:px-6 lg:px-8">
        <div className="flex w-full max-w-4xl flex-col gap-6">
          <div className="flex items-center justify-between gap-4 rounded-xl bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center gap-4">
              <button onClick={handleBack} className="p-2 bg-transparent text-[#64748b] hover:bg-gray-100 rounded-full transition-colors">
                <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: '"FILL" 0, "wght" 200, "GRAD" 0, "opsz" 48' }}>arrow_back</span>
              </button>
              <h1 className="text-[#101822] tracking-tight text-2xl sm:text-3xl font-bold leading-tight">
                {patientName}
              </h1>
              <button
                onClick={() => navigate(`/patient/${patient_id}`)}
                className="p-2 bg-transparent text-[#64748b] hover:bg-gray-100 rounded-full transition-colors"
                title="Editar información del paciente"
              >
                <span className="material-symbols-outlined text-2xl" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>edit</span>
              </button>
            </div>
            <button
              onClick={handleNewExam}
              className="flex min-w-[84px] max-w-[320px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-11 px-5 gap-2 bg-primary text-white text-base font-medium leading-normal whitespace-nowrap"
            >
              <span className="material-symbols-outlined">add</span>
              <span className="truncate hidden sm:inline">Nuevo Examen</span>
            </button>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-slate-500">
              Cargando historial...
            </div>
          )}

          {!loading && !error && sortedVisits.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-slate-600">
              No hay visitas registradas para este paciente.
            </div>
          )}

          <div className="flex flex-col gap-6">
            {activeVisit && (
              <div className="p-4 @container">
                <div className="flex flex-col rounded-xl shadow-lg bg-white border-2 border-[#FFC107]">
                  <div className="flex w-full grow flex-col gap-4 p-6">
                    <div className="flex items-center gap-4 justify-between flex-wrap">
                      <div className="flex items-center gap-4">
                          <p className="text-[#64748b] text-sm"></p>
                        <p className="text-[#101822] text-lg font-bold leading-tight tracking-[-0.015em]">
                          Consulta: {formatDate(activeVisit.created_at, false)}
                        </p>
                      </div>
                      <div className="flex items-center justify-center gap-2 rounded-full bg-[#FFC107]/20 px-3 py-1">
                        <span className="text-[#FF8F00] text-xl leading-none">⚠️</span>
                        <p className="text-[#FF8F00] text-sm font-medium leading-normal whitespace-nowrap">
                          {getStatusLabel(activeVisit.status)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 text-[#101822]">
                      <p className="text-base"><span className="font-semibold">Dx:</span> {activeVisit.diagnosis_snippet || 'Pendiente'}</p>
                    </div>
                    <div className="flex items-center justify-end pt-2">
                      <button
                        onClick={() => navigate(`/commercial-handoff/${activeVisit.id}`)}
                        className="flex min-w-[84px] w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 gap-2 bg-primary text-white text-base font-bold leading-normal hover:opacity-90 transition-opacity"
                      >
                        <span className="truncate">Continuar a Venta</span>
                        <span className="material-symbols-outlined">arrow_forward</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {pendingPaymentVisits.map((visit) => {
              const pendingAmount = (visit.sales_summary?.grand_total || 0) - (visit.sales_summary?.amount_paid || 0);
              return (
                <div key={visit.id} className="p-4 @container">
                  <div className="flex flex-col rounded-xl shadow-lg bg-white border-2 border-orange-400">
                    <div className="flex w-full grow flex-col gap-4 p-6">
                      <div className="flex items-center gap-4 justify-between flex-wrap">
                        <div className="flex items-center gap-4">
                          <p className="text-[#101822] text-lg font-bold leading-tight tracking-[-0.015em]">
                            {formatDate(visit.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center justify-center gap-2 rounded-full bg-orange-100 border border-orange-300 px-3 py-1">
                          <span className="text-orange-700 text-xl leading-none">💰</span>
                          <p className="text-orange-700 text-sm font-medium leading-normal whitespace-nowrap">
                            {getStatusLabel(visit.status)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 text-[#101822]">
                        <p className="text-base"><span className="font-semibold">Dx:</span> {visit.diagnosis_snippet || 'Sin diagnóstico'}</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <p><span className="text-gray-500">Total:</span> <span className="font-semibold">{formatCurrency(visit.sales_summary?.grand_total)}</span></p>
                          <p><span className="text-gray-500">Abonado:</span> <span className="font-semibold text-green-600">{formatCurrency(visit.sales_summary?.amount_paid)}</span></p>
                          <p><span className="text-gray-500">Pendiente:</span> <span className="font-bold text-orange-600">{formatCurrency(pendingAmount)}</span></p>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                          onClick={() => handleViewVisit(visit.id)}
                          className="flex items-center justify-center w-11 h-11 rounded-full bg-transparent text-[#64748b] hover:bg-gray-100 transition-colors"
                          title="Ver ficha clínica"
                        >
                          <span className="material-symbols-outlined" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>visibility</span>
                        </button>
                        <button
                          onClick={() => handleSendEmail(visit.id)}
                          className="flex items-center justify-center w-11 h-11 rounded-full bg-transparent text-[#64748b] hover:bg-gray-100 transition-colors"
                          title="Enviar por correo"
                        >
                          <span className="material-symbols-outlined" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>send</span>
                        </button>
                        <button
                          onClick={() => handlePrintWorkOrder(visit.id)}
                          disabled={isGenerating}
                          className="flex items-center justify-center w-11 h-11 rounded-full bg-transparent text-[#64748b] hover:bg-gray-100 transition-colors disabled:opacity-50"
                          title="Imprimir orden de trabajo"
                        >
                          <span className="material-symbols-outlined" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>print</span>
                        </button>
                        <button
                          onClick={() => navigate(`/commercial-handoff/${visit.id}`)}
                          className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 gap-2 bg-orange-500 text-white text-base font-bold leading-normal hover:bg-orange-600 transition-colors"
                        >
                          <span className="truncate">Completar Pago</span>
                          <span className="material-symbols-outlined">payments</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {pastVisits.map((visit) => {
              const badge = getBadgeStyles(visit.status);

              return (
                <div key={visit.id} className="px-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl p-6 bg-white shadow-[0_0_4px_rgba(0,0,0,0.05)] border border-slate-100">
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                      <p className="text-[#101822] text-base font-bold leading-tight">{formatDate(visit.created_at)}</p>
                      <p className="text-[#64748b] text-sm font-normal leading-normal">{historyTitleForVisit(visit)}</p>
                    </div>
                    {visit.sales_summary?.grand_total ? (
                      <p className="text-[#16a34a] text-base font-semibold leading-normal flex-[0.5_0.5_0px] text-right hidden sm:block">
                        Total: {formatCurrency(visit.sales_summary.grand_total)}
                      </p>
                    ) : null}
                    <div className="flex items-center flex-wrap gap-2">
                      <div className={`flex items-center justify-center gap-2 rounded-full border px-3 py-1 ${badge.container}`}>
                        <span className={badge.text}>{badge.icon}</span>
                        <span className={`${badge.text} text-sm font-medium`}>{getStatusLabel(visit.status)}</span>
                      </div>
                      <button
                        onClick={() => handleViewVisit(visit.id)}
                        className="flex items-center justify-center w-11 h-11 rounded-full bg-transparent text-[#64748b] hover:bg-gray-100 transition-colors"
                        title="Ver ficha clínica"
                      >
                        <span className="material-symbols-outlined" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>visibility</span>
                      </button>
                      <button
                        onClick={() => handleEditVisit(visit.id)}
                        className="flex items-center justify-center w-11 h-11 rounded-full bg-transparent text-[#64748b] hover:bg-gray-100 transition-colors"
                        title="Editar ficha clínica"
                      >
                        <span className="material-symbols-outlined" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>edit</span>
                      </button>
                      <button
                        onClick={() => handleSendEmail(visit.id)}
                        disabled={visit.status !== 'completed' && visit.status !== 'payment_pending'}
                        className="flex items-center justify-center w-11 h-11 rounded-full bg-transparent text-[#64748b] hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={(visit.status !== 'completed' && visit.status !== 'payment_pending') ? 'Solo disponible para visitas completadas' : 'Enviar por correo'}
                      >
                        <span className="material-symbols-outlined" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>send</span>
                      </button>
                      <button
                        onClick={() => handlePrintWorkOrder(visit.id)}
                        disabled={isGenerating || (visit.status !== 'completed' && visit.status !== 'payment_pending')}
                        className="hidden sm:flex items-center justify-center w-11 h-11 rounded-full bg-transparent text-[#64748b] hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={(visit.status !== 'completed' && visit.status !== 'payment_pending') ? 'Solo disponible para visitas completadas' : 'Imprimir orden de trabajo'}
                      >
                        <span className="material-symbols-outlined" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>print</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <EditChoiceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSelectClinicalChart={handleSelectClinicalChart}
        onSelectSales={handleSelectSales}
      />

      <SendEmailModal
        isOpen={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        visitId={selectedEmailVisitId || 0}
        patientEmail={patientData?.email}
        patientName={patientName}
      />

      <Footer />
    </div>
  );
};